import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import _ from 'lodash';
import { NamedAsyncAdapter } from '../named-async.adapter';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import {
    AdapterRegistry,
    format,
    LinkedService,
    IRelationship,
    IColumnRelationshipInformation,
    MappingType,
    ValueAdapter,
    Owned,
    linkedServiceReferenceToResource,
    Folder,
    Named
} from 'shell-models-lib';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { AdfDataMapper } from 'app/model/adfDataMapper/adfDataMapper';
import { IMapperTable, SchemaLoadStatus } from 'app/model/adfDataMapper/IMapperTable';
import { MapperConnection } from 'app/model/adfDataMapper/mapperConnection';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { DataFlowDataType, DecimalDataFlowType, IDataFlowDataType } from 'app/model/dataflow/dataflow-datatype';
import { MapperConnectionReference, MapperConnectionType } from 'app/model/adfDataMapper/mapperConnectionReference';
import { MapperAttributeMapping } from 'app/model/adfDataMapper/mapperAttributeMapping';
import {
    DataMapperMapping,
    IMapperJoinPair,
    IMapperTableDenormalizeEntity,
    IMapperTableDenormalizeInfo
} from 'app/model/adfDataMapper/dataMapperMapping';
import { MapperAttributeReference } from 'app/model/adfDataMapper/mapperAttributeReference';
import { DataFlowExpressionFunctions } from 'app/model/dataflow/dataflow-expression-functions';
import { MappingSourceConnectionInfo } from 'app/model/adfDataMapper/mappingSourceConnectionInfo';
import { MappingTargetConnectionInfo } from 'app/model/adfDataMapper/mappingTargetConnectionInfo';
import { MapperPolicy } from 'app/model/adfDataMapper/mapperPolicy';
import {
    MapperCadenceFrequency,
    MapperFrequencyMode,
    MapperPolicyRecurrence
} from 'app/model/adfDataMapper/mapperPolicyRecurrence';
import { MapperCompute } from 'app/model/adfDataMapper/mapperCompute';
import { DatasetTypeHandlerFactory } from 'app/components/create-datamapper/utils/datasetTypeHandlerFactory';
import * as Utils from 'app/model/adfDataMapper/adfDataMapperUtils';
import { DataFlowSchemaAdapterUtils } from '../dataflow/dataflow-schema-adapter-utils';
import { MapperStagingSettings } from 'app/model/adfDataMapper/mapperStagingSettings';
import * as tableUtils from 'app/model/adfDataMapper/mapperTable.utils';
import { getCachedServiceInstanceSync, getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { DataflowFormatRegistryService } from 'app/registries/dataflow/dataflow-format-registry.service';
import { LoggerService } from 'app/logger/logger.service';

const primitiveDataTypes = [
    'integer',
    'timestamp',
    'boolean',
    'binary',
    'decimal',
    'bool',
    'date',
    'double',
    'float',
    'int',
    'number',
    'long',
    'short',
    'string',
    'byte'
];

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AdfDataMapper),
    asynchronous: true
})
export class AdfDataMapperArtifactAsyncAdapter extends NamedAsyncAdapter<AdfDataMapper, Model.AdfDataMapperArtifact> {
    protected async _toResource(model: AdfDataMapper): Promise<Model.AdfDataMapperArtifact> {
        const resource = (await super._toResource(model)) as Model.AdfDataMapperArtifact;
        resource.name = model.name;

        resource.properties = {
            folder: this.context.adapterFor(Folder.MODEL).toResource(model.folder),
            description: model.description,
            SourceConnectionsInfo: [],
            TargetConnectionsInfo: [],
            Policy: this._toResourcePolicy(model.policy),
            status: model.runStatus,
            allowVNetOverride: model.allowVNetOverride,
            id: model.mapperId,
            Compute: this._toResourceCompute(model.compute)
        };

        if (!!model.sourceConnectionsInfo) {
            model.sourceConnectionsInfo.forEach((sourceConInfo) => {
                resource.properties.SourceConnectionsInfo.push({
                    SourceEntities: this._toResourceMapperTable(
                        sourceConInfo.tables,
                        sourceConInfo.connection.commonDslConnectorProperties,
                        false
                    ),
                    Connection: this._toResourceConnection(sourceConInfo.connection, sourceConInfo.tables)
                });
            });
        }

        if (!!model.nonCdcSourceConnectionsInfo) {
            model.nonCdcSourceConnectionsInfo.forEach((nonCdcSourceConInfo) => {
                resource.properties.SourceConnectionsInfo.push({
                    SourceEntities: this._toResourceMapperTable(
                        nonCdcSourceConInfo.tables,
                        nonCdcSourceConInfo.connection.commonDslConnectorProperties,
                        false
                    ),
                    Connection: this._toResourceConnection(nonCdcSourceConInfo.connection, nonCdcSourceConInfo.tables)
                });
            });
        }

        if (!!model.targetConnectionsInfo) {
            model.targetConnectionsInfo.forEach((targetConInfo) => {
                resource.properties.TargetConnectionsInfo.push({
                    TargetEntities: this._toResourceMapperTable(
                        targetConInfo.tables,
                        targetConInfo.connection.commonDslConnectorProperties,
                        true
                    ),
                    Connection: this._toResourceConnection(targetConInfo.connection, targetConInfo.tables),
                    DataMapperMappings: this._toResourceMapperMappings(targetConInfo.dataMapperMappings),
                    Relationships: this._toResourceRelationships(targetConInfo.relationships)
                });
            });
        }

        if (!!model.stagingSettings?.stagingLinkedService) {
            resource.properties.typeProperties = {
                staging: {
                    linkedService: linkedServiceReferenceToResource(
                        model.stagingSettings.stagingLinkedService,
                        this.context,
                        undefined,
                        this.ensureLogger()
                    ),
                    folderPath: model.stagingSettings?.stagingFolderPath
                },
                linkedServiceType: Metamodel.modelOf(model.stagingSettings.stagingLinkedService).name
            };
        }

        return resource;
    }

    protected async _toModel(resource: Model.AdfDataMapperArtifact, model?: AdfDataMapper): Promise<AdfDataMapper> {
        if (!model) {
            model = AdfDataMapper.MODEL.create<AdfDataMapper>();
        }

        model.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, resource.properties, AdfDataMapper.MODEL)
        );
        model = await this.parseJSONToModel(resource, model);
        return model;
    }

    protected async parseJSONToModel(
        resource: Model.AdfDataMapperArtifact,
        model: AdfDataMapper
    ): Promise<AdfDataMapper> {
        if (!resource || !resource.properties) {
            return model;
        }

        await super._toModel(resource.properties as unknown as Model.AdfDataMapperArtifact, model);
        model.name = resource.name;
        model.description = resource.properties.description;

        if (!!model.sourceConnectionsInfo) {
            model.sourceConnectionsInfo = [];
        }

        if (!!model.nonCdcSourceConnectionsInfo) {
            model.nonCdcSourceConnectionsInfo = [];
        }

        if (!!model.targetConnectionsInfo) {
            model.targetConnectionsInfo = [];
        }

        if (!!resource.properties.SourceConnectionsInfo) {
            for (const conInfo of resource.properties.SourceConnectionsInfo) {
                if (conInfo) {
                    const sourceConnectionInfo = await this._toModelSourceConnectionInfo(conInfo, model);
                    if (this._isCdcSource(sourceConnectionInfo)) {
                        model.sourceConnectionsInfo.push(sourceConnectionInfo);
                    } else {
                        model.nonCdcSourceConnectionsInfo.push(sourceConnectionInfo);
                    }
                }
            }
        }

        if (!!resource.properties.TargetConnectionsInfo) {
            for (const conInfo of resource.properties.TargetConnectionsInfo) {
                if (conInfo) {
                    model.targetConnectionsInfo.push(await this._toModelTargetConnectionInfo(conInfo, model));
                }
            }
        }

        if (!!resource.properties.typeProperties?.staging) {
            const linkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelByName(resource.properties.typeProperties.linkedServiceType),
                resource.properties.typeProperties.staging.linkedService, this.context
            );
            const stagingFolderPath = resource.properties.typeProperties.staging?.folderPath;
            model.stagingSettings = new MapperStagingSettings(linkedService, stagingFolderPath);
        }

        model.policy = this._toModelPolicy(resource.properties.Policy);
        model.compute = this._toModelCompute(resource.properties.Compute);
        model.allowVNetOverride = resource.properties.allowVNetOverride;
        model.runStatus = resource.properties.status;
        model.mapperId = resource.properties.id;
        model.folder = Owned.create<Folder>(Folder.MODEL, model);
        this.context.adapterFor(Folder.MODEL).toModel(resource.properties.folder, model.folder);

        return model;
    }

    private async _toModelSourceConnectionInfo(
        resourceSourceConnectionInfo: Model.MappingSourceConnectionInfoResource,
        model: AdfDataMapper
    ): Promise<MappingSourceConnectionInfo> {
        const modelConnection = this._toModelConnection(resourceSourceConnectionInfo.Connection);
        return new MappingSourceConnectionInfo(
            modelConnection,
            await this._toModelMapperTables(
                resourceSourceConnectionInfo.SourceEntities,
                resourceSourceConnectionInfo.Connection,
                modelConnection,
                model,
                true
            )
        );
    }

    private async _toModelTargetConnectionInfo(
        resourceTargetConnectionInfo: Model.MappingTargetConnectionInfoResource,
        model: AdfDataMapper
    ): Promise<MappingTargetConnectionInfo> {
        const modelConnection = this._toModelConnection(resourceTargetConnectionInfo.Connection);
        const modelTables = await this._toModelMapperTables(
            resourceTargetConnectionInfo.TargetEntities,
            resourceTargetConnectionInfo.Connection,
            modelConnection,
            model,
            false
        );
        const targetConnectionInfo = new MappingTargetConnectionInfo(
            modelConnection,
            modelTables,
            this._toModelMapperMappings(
                resourceTargetConnectionInfo.DataMapperMappings,
                modelTables,
                modelConnection,
                model
            ),
            this._toModelRelationships(resourceTargetConnectionInfo.Relationships)
        );

        this._handleTargetSchemaPropogationIfRequired(targetConnectionInfo);

        return targetConnectionInfo;
    }

    private _toModelMapperMappings(
        resourceMapperMappings: Model.AdfDataMapperMappingResource[],
        targetTables: IMapperTable[],
        targetConnection: MapperConnection,
        model: AdfDataMapper
    ): DataMapperMapping[] {
        const modelMapperMappings: DataMapperMapping[] = [];

        if (resourceMapperMappings) {
            resourceMapperMappings.forEach((resourceMapperMapping) => {
                const targetTable = tableUtils.getMapperTable(targetTables, resourceMapperMapping.targetEntityName);
                const keys: string[] = tableUtils.getDslPropertyValue(
                    targetTable?.dslConnectorProperties,
                    DslConnectorConstants.keys
                );
                const primaryKeysColumnsSet = new Set<string>();
                if (!!keys && keys.length > 0) {
                    keys.forEach((key) => primaryKeysColumnsSet.add(key));
                }
                modelMapperMappings.push(
                    this._toModelMapperMapping(
                        resourceMapperMapping,
                        primaryKeysColumnsSet,
                        targetTable,
                        targetConnection,
                        model
                    )
                );
            });
        }

        return modelMapperMappings;
    }

    private _toModelMapperMapping(
        resourceMapperMapping: Model.AdfDataMapperMappingResource,
        primaryKeysColumnsSet: Set<string>,
        targetTable: IMapperTable,
        targetConnection: MapperConnection,
        model: AdfDataMapper
    ): DataMapperMapping {
        if (!resourceMapperMapping) {
            return null;
        }

        const modelMapperMapping: DataMapperMapping = Named.create<DataMapperMapping>(DataMapperMapping.MODEL, model);
        modelMapperMapping.name = model.name;

        // fill the target details
        modelMapperMapping.targetTable = targetTable;
        modelMapperMapping.targetConnectionReference = targetConnection.getConnectionReference();

        // fill the source details
        modelMapperMapping.sourceConnectionReference = this._toModelConnectionReference(
            resourceMapperMapping.sourceConnectionReference
        );
        const sourceConnectionInfo = tableUtils.getTableConnectionInfo(
            model.sourceConnectionsInfo,
            modelMapperMapping.sourceConnectionReference
        );
        modelMapperMapping.sourceTable = sourceConnectionInfo
            ? tableUtils.getMapperTable(sourceConnectionInfo.tables, resourceMapperMapping.sourceEntityName)
            : undefined;

        // fill denormailzation info
        modelMapperMapping.sourceDenormalizeInfo = this._toModelSourceDenormalizationInfo(
            resourceMapperMapping.sourceDenormalizeInfo,
            model
        );

        // fill attribute mappings
        modelMapperMapping.attributeMappings = this._toModelAttributeMappings(
            resourceMapperMapping.attributeMappingInfo.attributeMappings,
            primaryKeysColumnsSet,
            model
        );

        return modelMapperMapping;
    }

    private _toModelSourceDenormalizationInfo(
        resourceSourceDenormalizationInfo: Model.MapperTableDenormalizeInfoResource,
        model: AdfDataMapper
    ): IMapperTableDenormalizeInfo {
        if (!!resourceSourceDenormalizationInfo) {
            return {
                entities: this._toModelTableDenormalizeEntities(resourceSourceDenormalizationInfo.entities, model)
            };
        }

        return undefined;
    }

    private _toModelTableDenormalizeEntities(
        resourceEntities: Model.MapperTableDenormalizeEntityResource[],
        model: AdfDataMapper
    ): IMapperTableDenormalizeEntity[] {
        const modelEntities: IMapperTableDenormalizeEntity[] = [];

        if (!!resourceEntities) {
            resourceEntities.forEach((resourceEntity) =>
                modelEntities.push(this._toModelTableDenormalizeEntity(resourceEntity, model))
            );
        }

        return modelEntities;
    }

    private _toModelTableDenormalizeEntity(
        resourceEntity: Model.MapperTableDenormalizeEntityResource,
        model: AdfDataMapper
    ): IMapperTableDenormalizeEntity {
        if (!!resourceEntity) {
            return {
                joinPairs: this._toModelJoinPairs(resourceEntity.joinPairs, model),
                toEntity: resourceEntity.toEntity,
                toEntityConnectionReference: this._toModelConnectionReference(
                    resourceEntity.toEntityConnectionReference
                )
            };
        }

        return undefined;
    }

    private _toModelJoinPairs(
        resourceJoinPairs: Model.MapperJoinPairResource[],
        model: AdfDataMapper
    ): IMapperJoinPair[] {
        const modelJoinPairs: IMapperJoinPair[] = [];

        if (!!resourceJoinPairs) {
            resourceJoinPairs.forEach((resourceJoinPair) =>
                modelJoinPairs.push(this._toModelJoinPair(resourceJoinPair, model))
            );
        }

        return modelJoinPairs;
    }

    private _toModelJoinPair(resourceJoinPair: Model.MapperJoinPairResource, model: AdfDataMapper): IMapperJoinPair {
        if (!!resourceJoinPair) {
            const isNonCdcConnection = this._isNonCdcSourceConnection(
                resourceJoinPair.fromAttribute.entityConnectionReference,
                model
            );
            return {
                fromAttribute: this._toModelAttributeReference(
                    resourceJoinPair.fromAttribute,
                    isNonCdcConnection,
                    model
                ),
                toAttribute: resourceJoinPair.toAttribute,
                operator: resourceJoinPair.operator,
                expression: resourceJoinPair.expression
            };
        }

        return undefined;
    }

    private _toModelConnectionReference(
        resourceConnectionReference: Model.MapperConnectionReferenceResource
    ): MapperConnectionReference {
        if (!!resourceConnectionReference) {
            return new MapperConnectionReference(
                resourceConnectionReference.connectionName,
                resourceConnectionReference.type
            );
        }

        return undefined;
    }

    private _toModelAttributeMappings(
        attributeMappings: Model.AdfDataMapperTableAttributeMappingResource[],
        primaryKeysColumnsSet: Set<string>,
        model: AdfDataMapper
    ): MapperAttributeMapping[] {
        const modelAttributeMappings: MapperAttributeMapping[] = [];

        if (!!attributeMappings) {
            attributeMappings.forEach((resourceAttributeMapping) => {
                modelAttributeMappings.push(
                    this._toModelAttributeMapping(resourceAttributeMapping, primaryKeysColumnsSet, model)
                );
            });
        }

        return modelAttributeMappings;
    }

    private _toModelAttributeMapping(
        resourceAttributeMapping: Model.AdfDataMapperTableAttributeMappingResource,
        primaryKeysColumnsSet: Set<string>,
        model: AdfDataMapper
    ): MapperAttributeMapping {
        if (!resourceAttributeMapping) {
            return null;
        }

        const modelAttributeMapping: MapperAttributeMapping = new MapperAttributeMapping(resourceAttributeMapping.name);

        // Comparision of expression might be needed to map it back correctly.
        modelAttributeMapping.mappingType = this._toModelMappingType(
            resourceAttributeMapping.type,
            resourceAttributeMapping.functionName
        );

        modelAttributeMapping.attributeReference =
            modelAttributeMapping.mappingType === MappingType.DERIVED &&
            resourceAttributeMapping.attributeReferences?.length > 0
                ? this._toModelAttributeReference(
                      resourceAttributeMapping.attributeReferences[0],
                      this._isNonCdcSourceConnection(
                          resourceAttributeMapping.attributeReferences[0].entityConnectionReference,
                          model
                      ),
                      model
                  )
                : this._toModelAttributeReference(
                      resourceAttributeMapping.attributeReference,
                      this._isNonCdcSourceConnection(
                          resourceAttributeMapping.attributeReference?.entityConnectionReference,
                          model
                      ),
                      model
                  );

        modelAttributeMapping.attributeReferences = this._toModelAttributeReferences(
            resourceAttributeMapping.attributeReferences,
            model
        );

        // TODO: Check if there loss of data in ths conversion.
        modelAttributeMapping.expression = new DataFlowExpression();
        modelAttributeMapping.expression.expression = resourceAttributeMapping.expression;

        if (!!resourceAttributeMapping.expression) {
            const expression = modelAttributeMapping.expression as DataFlowExpression;
            expression.returnType = Utils.getReturnDataTypeForExpression(resourceAttributeMapping.expression);
        }

        modelAttributeMapping.alternateKeys = !!resourceAttributeMapping.alternateKeys
            ? resourceAttributeMapping.alternateKeys.map((ak) => ak)
            : null;
        modelAttributeMapping.lookupFrom = resourceAttributeMapping.lookupFrom;
        modelAttributeMapping.unpivotInfo = resourceAttributeMapping.unpivotInfo;
        modelAttributeMapping.functionName = resourceAttributeMapping.functionName;
        modelAttributeMapping.isPrimaryKeyColumn = primaryKeysColumnsSet.has(resourceAttributeMapping.name);

        return modelAttributeMapping;
    }

    private _toModelMappingType(resourceMappingType: string, functionName: string): string {
        if (resourceMappingType) {
            if (resourceMappingType === MappingType.DERIVED) {
                if (
                    functionName !== DataFlowExpressionFunctions.UPPER.name &&
                    functionName !== DataFlowExpressionFunctions.LOWER.name &&
                    functionName !== DataFlowExpressionFunctions.TRIM.name
                ) {
                    return MappingType.ADVANCED;
                }
            }
        }

        return resourceMappingType;
    }

    private _toModelAttributeReferences(
        resourceEntityReferences: Model.AdfDataMapperAttributeReferenceResource[],
        model: AdfDataMapper
    ): MapperAttributeReference[] {
        const modelAttributeReferences: MapperAttributeReference[] = [];

        if (resourceEntityReferences) {
            resourceEntityReferences.forEach((resourceEntityReference) => {
                const isNonCdcConnection = this._isNonCdcSourceConnection(
                    resourceEntityReference.entityConnectionReference,
                    model
                );
                modelAttributeReferences.push(
                    this._toModelAttributeReference(resourceEntityReference, isNonCdcConnection, model)
                );
            });
        }

        return modelAttributeReferences;
    }

    private _toModelAttributeReference(
        resourceEntityReference: Model.AdfDataMapperAttributeReferenceResource,
        isNonCdcSource: boolean,
        model: AdfDataMapper
    ): MapperAttributeReference {
        if (!resourceEntityReference) {
            return null;
        }

        const modelConnectionReference = this._toModelConnectionReference(
            resourceEntityReference.entityConnectionReference
        );
        const sourceTable = this._getSourceTable(
            model,
            resourceEntityReference.entity,
            modelConnectionReference,
            isNonCdcSource
        );

        const modelAttributeReference: MapperAttributeReference = new MapperAttributeReference(
            resourceEntityReference.name,
            sourceTable,
            modelConnectionReference
        );

        return modelAttributeReference;
    }

    private _toModelRelationships(resourceRelationships: Model.MappingArtifactEntityRelationship[]): IRelationship[] {
        const modelRelationships: IRelationship[] = [];

        if (resourceRelationships) {
            resourceRelationships.forEach((resourceRelationship) => {
                modelRelationships.push(this._toModelRelationship(resourceRelationship));
            });
        }

        return modelRelationships;
    }

    private _toModelRelationship(resourceRelationship: Model.MappingArtifactEntityRelationship): IRelationship {
        if (!resourceRelationship) {
            return null;
        }

        return {
            fromTableName: resourceRelationship.fromEntity,
            toTableName: resourceRelationship.toEntity,
            columnRelationshipInformations: !!resourceRelationship.attributePairs
                ? resourceRelationship.attributePairs.map((colRel) =>
                      this._toModelColumnRelationshipInformation(colRel)
                  )
                : [],
            name: undefined,
            namespace: undefined,
            relationshipType: undefined
        };
    }

    private _toModelColumnRelationshipInformation(
        attributePairs: Model.MappingArtifactAttributePairs
    ): IColumnRelationshipInformation {
        if (!attributePairs) {
            return null;
        }

        return {
            fromColumnName: attributePairs.fromAttribute,
            toColumnName: attributePairs.toAttribute
        };
    }

    private _toModelConnection(resourceConnection: Model.AdfDataMapperConnectionResource): MapperConnection {
        if (resourceConnection) {
            const modelMapperConnection = new MapperConnection(resourceConnection.type);
            modelMapperConnection.linkedService = this._getLinkedService(resourceConnection);
            modelMapperConnection.linkedServiceType = resourceConnection.linkedServiceType;
            modelMapperConnection.commonDslConnectorProperties = resourceConnection.commonDslConnectorProperties
                ? this._toModelDslConnectorProperties(resourceConnection.commonDslConnectorProperties)
                : undefined;
            return modelMapperConnection;
        }

        return null;
    }

    private async _toModelMapperTables(
        mapperTableResources: Model.MapperTableResource[],
        resourceConnection: Model.AdfDataMapperConnectionResource,
        modelConnection: MapperConnection,
        model: AdfDataMapper,
        isSource: Boolean
    ): Promise<IMapperTable[]> {
        const mapperTables: IMapperTable[] = [];
        if (mapperTableResources) {
            for (const tableResource of mapperTableResources) {
                if (tableResource) {
                    mapperTables.push(
                        await this._toModelMapperTable(
                            tableResource,
                            resourceConnection,
                            modelConnection,
                            model,
                            isSource
                        )
                    );
                }
            }
        }

        return mapperTables;
    }

    private async _toModelMapperTable(
        resourceTable: Model.MapperTableResource,
        resourceConnection: Model.AdfDataMapperConnectionResource,
        modelConnection: MapperConnection,
        model: AdfDataMapper,
        isSource: Boolean
    ): Promise<IMapperTable> {
        if (!resourceTable) {
            return null;
        }

        const dataFlowSource: DataFlowSource = Owned.create<DataFlowSource>(DataFlowSource.MODEL, model);
        const resourceTableDslConnectorProperties =
            !!resourceTable.properties && !!resourceTable.properties.dslConnectorProperties
                ? resourceTable.properties.dslConnectorProperties
                : null;
        const modelTableDslConnectorProperties = this._toModelDslConnectorProperties(
            resourceTableDslConnectorProperties
        );
        const commonDslConnectorProperties = modelConnection.commonDslConnectorProperties;
        const mergedDictionary = tableUtils.mergeDictionaries(
            modelTableDslConnectorProperties,
            commonDslConnectorProperties
        );
        const formatString = this._getDslPropertyValue(mergedDictionary, DslConnectorConstants.format);

        dataFlowSource.name = Utils.getValidTransformationName(isSource, resourceTable.name);
        if (!!resourceTable.properties && !!resourceTable.properties.schema) {
            resourceTable.properties.schema.forEach((col) => {
                if (!primitiveDataTypes.includes(col.dataType)) {
                    const columnNameWithoutSpace = col.name?.replace(/[ ]/gi, '');
                    const schemaString = 'output(' + columnNameWithoutSpace + ' as ' + col.dataType + ')';
                    const schemaObj = DataFlowSchemaAdapterUtils.getSchemaFromString(schemaString);
                    schemaObj[0].name = col.name;
                    dataFlowSource.schema.push(schemaObj[0]);
                } else {
                    const dataflowColumn: DataFlowColumn = DataFlowColumn.MODEL.create();
                    dataflowColumn.name = col.name;
                    dataflowColumn.columnType = col.dataType;
                    dataflowColumn.dataFlowColumnType = this._toDataFlowColumnType(col.dataType);
                    dataFlowSource.schema.push(dataflowColumn);
                }
            });
        }

        dataFlowSource.sourceLinkedService = modelConnection.linkedService;

        const mapperTable = dataFlowSource as IMapperTable;
        mapperTable.isInlineDataset = resourceConnection.isInlineDataset;
        mapperTable.datasetType = await this._toModelDatasetType(
            modelConnection.linkedService,
            formatString,
            resourceTable.name
        );
        mapperTable.tableName = resourceTable.name;

        if (!!dataFlowSource.schema && dataFlowSource.schema.length > 0) {
            mapperTable.schemaLoadStatus = SchemaLoadStatus.LOADED;
            mapperTable.isFirstLoad = false;
        } else {
            mapperTable.schemaLoadStatus = SchemaLoadStatus.NOT_STARTED;
        }

        if (resourceTableDslConnectorProperties) {
            mapperTable.dslConnectorProperties = this._toModelDslConnectorProperties(
                resourceTableDslConnectorProperties
            );

            // although we are setting schema drift in dataMapperMappings as well in this file, here also it ius required to handle unmapped tables
            tableUtils.setAllowSchemaDrift(mapperTable, dataFlowSource.schema?.length > 0 ? false : true);

            // initialize dataset & dataset-settings
            DatasetTypeHandlerFactory.createDatasetTypeHandler(mapperTable.datasetType, isSource).then(
                (datasetTypeHandler) => {
                    if (datasetTypeHandler) {
                        mapperTable.datasetSettings = datasetTypeHandler.getDatasetSettings(
                            mergedDictionary,
                            mapperTable
                        );
                        mapperTable.sourceDataset = datasetTypeHandler.getDatasetForPreview(
                            mergedDictionary,
                            modelConnection.linkedService
                        );
                    } else {
                        throw new Error(format(stringResources.FailedToFindDatasetType, mapperTable.datasetType));
                    }
                }
            );
        }

        return mapperTable;
    }

    private async _toModelDatasetType(
        linkedService: LinkedService,
        formatString: string,
        sourceName: string
    ): Promise<DatasetFormatType> {
        const dataflowFormatRegistryService = await getServiceInstance<DataflowFormatRegistryService>(
            this.context.contextData,
            'DataflowFormatRegistryService'
        );
        const datasetTypeModelName = dataflowFormatRegistryService.getInlineSettingsModelName(
            formatString,
            linkedService
        );
        const datasetTypeModel = Metamodel.modelByName(datasetTypeModelName);
        const datasetFormatType = datasetTypeModel?.construct?.prototype?.getFormat();
        if (!datasetFormatType) {
            throw new Error(format(stringResources.InvalidDatasetType, formatString, sourceName));
        }
        return datasetFormatType;
    }

    private _toModelDslConnectorProperties(
        additionalPropertiesResources: Model.MapperTableAdditionalPropertiesResource[]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): Map<string, any> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const additionalPropertiesModels = new Map<string, any>();

        if (additionalPropertiesResources) {
            additionalPropertiesResources.forEach((res) => additionalPropertiesModels.set(res.name, res.value));
        }

        return additionalPropertiesModels;
    }

    private _toModelCompute(resourceCompute: Model.MapperComputeResource): MapperCompute {
        if (!!resourceCompute) {
            return new MapperCompute(resourceCompute.coreCount, resourceCompute.computeType);
        }

        return undefined;
    }

    private _toModelPolicy(resourcePolicy: Model.MapperPolicyResource): MapperPolicy {
        if (!!resourcePolicy) {
            const policy = new MapperPolicy();
            policy.recurrence = this._toModelPolicyRecurrence(resourcePolicy.recurrence);
            policy.mode = this._toModelPolicyMode(resourcePolicy.mode);
            return policy;
        }

        return undefined;
    }

    private _toModelPolicyMode(resourceMode: string): MapperFrequencyMode {
        if (!!resourceMode) {
            if (resourceMode.toLowerCase() === MapperFrequencyMode.Realtime.toLowerCase()) {
                return MapperFrequencyMode.Realtime;
            }
        }

        return MapperFrequencyMode.Microbatch;
    }

    private _toModelPolicyRecurrence(
        resourcePolicyRecurrence: Model.MapperPolicyRecurrenceResource
    ): MapperPolicyRecurrence {
        if (!!resourcePolicyRecurrence) {
            return new MapperPolicyRecurrence(
                this._convertToMapperCadenceFrequency(resourcePolicyRecurrence.frequency),
                resourcePolicyRecurrence.interval
            );
        }

        return undefined;
    }

    private _toResourceConnection(
        modelConnection: MapperConnection,
        tables: IMapperTable[]
    ): Model.AdfDataMapperConnectionResource {
        if (modelConnection && tables && tables.length > 0) {
            if (modelConnection.type === MapperConnectionType.LinkedServiceType) {
                return {
                    linkedService: linkedServiceReferenceToResource(
                        modelConnection.linkedService,
                        this.context,
                        undefined,
                        getCachedServiceInstanceSync<LoggerService>(this.context.contextData, 'LoggerService')
                    ),
                    linkedServiceType: modelConnection.linkedServiceType
                        ? modelConnection.linkedServiceType
                        : modelConnection.linkedService?.getLinkedServiceType(),
                    type: MapperConnectionType.LinkedServiceType,
                    isInlineDataset: tables[0].isInlineDataset,
                    commonDslConnectorProperties:
                        modelConnection.commonDslConnectorProperties.size > 0
                            ? this._toResourceDslConnectorProperties(modelConnection.commonDslConnectorProperties)
                            : undefined
                };
            }
        }

        return null;
    }

    private _toResourceCompute(modelCompute: MapperCompute): Model.MapperComputeResource {
        if (!!modelCompute) {
            return {
                coreCount: modelCompute.coreCount,
                computeType: modelCompute.computeType
            };
        }

        return undefined;
    }

    private _toResourcePolicy(modelPolicy: MapperPolicy): Model.MapperPolicyResource {
        if (!!modelPolicy) {
            return {
                recurrence: this._toResourcePolicyRecurrence(modelPolicy.recurrence),
                mode: modelPolicy.mode
            };
        }

        return undefined;
    }

    private _toResourcePolicyRecurrence(
        modelPolicyRecurrence: MapperPolicyRecurrence
    ): Model.MapperPolicyRecurrenceResource {
        if (!!modelPolicyRecurrence) {
            return {
                frequency: modelPolicyRecurrence.frequency,
                interval: modelPolicyRecurrence.interval
            };
        }

        return undefined;
    }

    private _toResourceMapperMappings(modelMapperMappings: DataMapperMapping[]): Model.AdfDataMapperMappingResource[] {
        const mapperMappings: Model.AdfDataMapperMappingResource[] = [];

        if (modelMapperMappings) {
            modelMapperMappings.forEach((dataMapperMapping) => {
                const sourceDenormInfo =
                    !!dataMapperMapping.sourceDenormalizeInfo &&
                    !!dataMapperMapping.sourceDenormalizeInfo.entities &&
                    dataMapperMapping.sourceDenormalizeInfo.entities.length
                        ? dataMapperMapping.sourceDenormalizeInfo
                        : undefined;

                mapperMappings.push({
                    targetEntityName: dataMapperMapping.targetTable?.tableName,
                    sourceEntityName: dataMapperMapping.sourceTable?.tableName,
                    sourceConnectionReference: dataMapperMapping.sourceConnectionReference,
                    sourceDenormalizeInfo: dataMapperMapping.isSourceTargetDrifted()
                        ? undefined
                        : this._toResourceSourceDenormalizationInfo(sourceDenormInfo),
                    attributeMappingInfo: {
                        attributeMappings: dataMapperMapping.isSourceTargetDrifted()
                            ? []
                            : dataMapperMapping.attributeMappings.map((attributeMapping) =>
                                  this._toResourceAttributeMappings(attributeMapping)
                              )
                    }
                });
            });
        }

        return mapperMappings;
    }

    private _toResourceSourceDenormalizationInfo(
        modelSourceDenormalizationInfo: IMapperTableDenormalizeInfo
    ): Model.MapperTableDenormalizeInfoResource {
        if (!!modelSourceDenormalizationInfo) {
            return {
                entities: this._toResourceTableDenormalizeEntities(modelSourceDenormalizationInfo.entities)
            };
        }

        return undefined;
    }

    private _toResourceTableDenormalizeEntities(
        modelEntities: IMapperTableDenormalizeEntity[]
    ): Model.MapperTableDenormalizeEntityResource[] {
        const resourceEntities: Model.MapperTableDenormalizeEntityResource[] = [];

        if (!!modelEntities) {
            modelEntities.forEach((resourceEntity) =>
                resourceEntities.push(this._toResourceTableDenormalizeEntity(resourceEntity))
            );
        }

        return resourceEntities;
    }

    private _toResourceTableDenormalizeEntity(
        modelEntity: IMapperTableDenormalizeEntity
    ): Model.MapperTableDenormalizeEntityResource {
        if (!!modelEntity) {
            return {
                joinPairs: this._toResourceJoinPairs(modelEntity.joinPairs),
                toEntity: modelEntity.toEntity,
                toEntityConnectionReference: this._toResourceConnectionReference(
                    modelEntity.toEntityConnectionReference
                )
            };
        }

        return undefined;
    }

    private _toResourceJoinPairs(modelJoinPairs: IMapperJoinPair[]): Model.MapperJoinPairResource[] {
        const resourceJoinPairs: Model.MapperJoinPairResource[] = [];

        if (!!modelJoinPairs) {
            modelJoinPairs.forEach((resourceJoinPair) =>
                resourceJoinPairs.push(this._toResourceJoinPair(resourceJoinPair))
            );
        }

        return resourceJoinPairs;
    }

    private _toResourceJoinPair(modelJoinPair: IMapperJoinPair): Model.MapperJoinPairResource {
        if (!!modelJoinPair) {
            return {
                fromAttribute: this._toResourceAttributeReference(modelJoinPair.fromAttribute),
                toAttribute: modelJoinPair.toAttribute,
                operator: modelJoinPair.operator,
                expression: modelJoinPair.expression
            };
        }

        return undefined;
    }

    private _toResourceAttributeReference(
        modelAttribute: MapperAttributeReference
    ): Model.AdfDataMapperAttributeReferenceResource {
        if (!!modelAttribute) {
            return {
                name: modelAttribute.columnName,
                entity: modelAttribute.table?.tableName,
                entityConnectionReference: this._toResourceConnectionReference(modelAttribute.tableConnectionReference)
            };
        }

        return undefined;
    }

    private _toResourceConnectionReference(
        modelConnectionReference: MapperConnectionReference
    ): Model.MapperConnectionReferenceResource {
        if (!!modelConnectionReference) {
            return {
                connectionName: modelConnectionReference.connectionName,
                type: modelConnectionReference.type
            };
        }

        return undefined;
    }

    private _toResourceMapperTable(
        tables: IMapperTable[],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        commonDslConnectorProperties: Map<string, any>,
        isSink: boolean
    ): Model.MapperTableResource[] {
        const resourceTables: Model.MapperTableResource[] = [];
        if (tables) {
            tables.forEach((table: IMapperTable) => {
                const dataFlowSource = table as unknown as DataFlowSource;

                if (dataFlowSource && dataFlowSource.schema !== null) {
                    const tableSpecificDslConnectorProperties = this._removeRedundantTableDslConnectorProperties(
                        table.dslConnectorProperties,
                        commonDslConnectorProperties
                    );
                    const propogateSchema = this._doesSchemaNeedsToBePropogated(table, isSink);
                    const resourceTable: Model.MapperTableResource = {
                        name: table.tableName,
                        properties: {
                            schema: propogateSchema ? this._toResourceSchema(dataFlowSource.schema) : [],
                            dslConnectorProperties: this._toResourceDslConnectorProperties(
                                tableSpecificDslConnectorProperties
                            )
                        }
                    };

                    if (isSink && !propogateSchema) {
                        this._adjustSchemaDriftForTarget(resourceTable);
                    }
                    resourceTables.push(resourceTable);
                }
            });
        }

        return resourceTables;
    }

    private _toResourceSchema(
        modelColumns: DataFlowColumn[]
    ): Model.MappingArtifactSourceSinkSchemaPropertiesResource[] {
        if (modelColumns) {
            return modelColumns.map((col) => {
                return {
                    name: col.name,
                    dataType: Utils.getQualifiedTypeString(col)
                };
            });
        }

        return [];
    }

    private _toResourceDslConnectorProperties(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        modelDslConnectorProperties: Map<string, any>
    ): Model.MappingArtifactSourceSinkDslConnectorPropertiesResource[] {
        if (modelDslConnectorProperties) {
            return Array.from(modelDslConnectorProperties).map(([key, val]) => {
                return {
                    name: key,
                    value: val
                };
            });
        }

        return [];
    }

    private _toResourceRelationships(relationships: IRelationship[]): Model.MappingArtifactEntityRelationship[] {
        return !!relationships && relationships.length > 0
            ? relationships.map((rel) => this._toResourceRelationship(rel))
            : [];
    }

    private _toResourceRelationship(relationship: IRelationship): Model.MappingArtifactEntityRelationship {
        if (!relationship) {
            return null;
        }

        return {
            fromEntity: relationship.fromTableName,
            toEntity: relationship.toTableName,
            attributePairs: this._toResourceAttributePairsList(relationship.columnRelationshipInformations)
        };
    }

    private _toResourceAttributePairsList(
        columnRelationships: IColumnRelationshipInformation[]
    ): Model.MappingArtifactAttributePairs[] {
        return !!columnRelationships && columnRelationships.length > 0
            ? columnRelationships.map((colRel) => this._toResourceAttributePairs(colRel))
            : [];
    }

    private _toResourceAttributePairs(
        columnRelationshipInformation: IColumnRelationshipInformation
    ): Model.MappingArtifactAttributePairs {
        if (!columnRelationshipInformation) {
            return null;
        }

        return {
            fromAttribute: columnRelationshipInformation.fromColumnName,
            toAttribute: columnRelationshipInformation.toColumnName
        };
    }

    private _toResourceAttributeMappings(
        modelAttributeMapping: MapperAttributeMapping
    ): Model.AdfDataMapperTableAttributeMappingResource {
        const mappingType = modelAttributeMapping.mappingType;
        const resourceAttributeMapping: Model.AdfDataMapperTableAttributeMappingResource = {
            name: modelAttributeMapping.name,
            type:
                modelAttributeMapping.mappingType === MappingType.ADVANCED
                    ? MappingType.DERIVED
                    : modelAttributeMapping.mappingType
        };

        resourceAttributeMapping.functionName = modelAttributeMapping.functionName;

        switch (mappingType) {
            case MappingType.ADVANCED:
                if (modelAttributeMapping.expression && !!modelAttributeMapping.expression.expression) {
                    resourceAttributeMapping.expression = modelAttributeMapping.expression.expression;
                    // TODO nimoolen - fix this
                    resourceAttributeMapping.attributeReferences = modelAttributeMapping.attributeReferences.map(
                        (ref) => {
                            return {
                                name: ref.columnName,
                                entity: ref.table?.tableName,
                                entityConnectionReference: ref.tableConnectionReference
                            };
                        }
                    );
                }
                break;
            case MappingType.AGGREGATE:
            case MappingType.DERIVED:
                resourceAttributeMapping.expression = `${modelAttributeMapping.functionName}(${modelAttributeMapping.attributeReference.columnName})`;
                resourceAttributeMapping.attributeReferences = [
                    {
                        name: modelAttributeMapping.attributeReference.columnName,
                        entity: modelAttributeMapping.attributeReference.table?.tableName,
                        entityConnectionReference: modelAttributeMapping.attributeReference.tableConnectionReference
                    }
                ];
                break;
            case MappingType.SURROGATEKEY:
                resourceAttributeMapping.alternateKeys = modelAttributeMapping.alternateKeys;
                break;
            case MappingType.LOOKUP:
                resourceAttributeMapping.lookupFrom = modelAttributeMapping.lookupFrom;
                break;
            case MappingType.UNPIVOT:
                resourceAttributeMapping.unpivotInfo = modelAttributeMapping.unpivotInfo;
                break;
            default:
                if (modelAttributeMapping.attributeReference && !!modelAttributeMapping.attributeReference.columnName) {
                    resourceAttributeMapping.attributeReference = {
                        name: modelAttributeMapping.attributeReference.columnName,
                        entity: modelAttributeMapping.attributeReference.table?.tableName,
                        entityConnectionReference: modelAttributeMapping.attributeReference.tableConnectionReference
                    };
                }
                break;
        }
        return resourceAttributeMapping;
    }

    private _handleTargetSchemaPropogationIfRequired(targetConnectionInfo: MappingTargetConnectionInfo): void {
        targetConnectionInfo.dataMapperMappings.forEach((dataMapperMapping) => {
            const sourceTable = dataMapperMapping.sourceTable;
            const targetTable = dataMapperMapping.targetTable;

            if (!sourceTable || !targetTable) {
                return;
            }

            const sourceDataFlowSource = sourceTable as DataFlowSource;
            const targetDataFlowSource = targetTable as DataFlowSource;

            // set schema drift property correctly
            const isSourceTargetDrifted =
                sourceDataFlowSource.schema.length > 0 || targetDataFlowSource.schema.length > 0 ? false : true;
            tableUtils.setAllowSchemaDrift(sourceTable, isSourceTargetDrifted);
            tableUtils.setAllowSchemaDrift(targetTable, isSourceTargetDrifted);

            // check if the target schema is empty
            if (this._isSourceToTargetSchemaPropogationRequired(dataMapperMapping, targetDataFlowSource)) {
                // copy the source schema to target
                tableUtils.copySourceSchemaToTargetSchema(sourceDataFlowSource, targetDataFlowSource);

                // modify target table columns data type based on ADVANCED attribute mappings
                this._updateTargetSchemaAsPerMappingsIfRequired(dataMapperMapping, targetDataFlowSource);
            }
        });
    }

    private _doesSchemaNeedsToBePropogated(table: IMapperTable, isSink: boolean): boolean {
        if (table.isSchemaDrifted === true) {
            return false;
        }

        if (isSink && table.isSchemaDrifted === false && table.isFirstLoad === true) {
            return false;
        }

        return true;
    }

    private _adjustSchemaDriftForTarget(resourceTable: Model.MapperTableResource): void {
        let resourceDslProperty = resourceTable.properties.dslConnectorProperties.find(
            (dslProperty) => dslProperty.name === DslConnectorConstants.allowSchemaDrift
        );
        if (!!resourceDslProperty) {
            resourceDslProperty.value = true;
        }

        resourceDslProperty = resourceTable.properties.dslConnectorProperties.find(
            (dslProperty) => dslProperty.name === DslConnectorConstants.inferDriftedColumnTypes
        );
        if (!!resourceDslProperty) {
            resourceDslProperty.value = true;
        }
    }

    private _isSourceToTargetSchemaPropogationRequired(
        dataMapperMapping: DataMapperMapping,
        targetDataFlowSource: DataFlowSource
    ): boolean {
        return dataMapperMapping.attributeMappings.length > 0 &&
            (!targetDataFlowSource || !targetDataFlowSource.schema || targetDataFlowSource.schema.length === 0)
            ? true
            : false;
    }

    private _updateTargetSchemaAsPerMappingsIfRequired(
        dataMapperMapping: DataMapperMapping,
        targetDataFlowSource: DataFlowSource
    ): void {
        dataMapperMapping.attributeMappings.forEach((attributeMapping) => {
            if (attributeMapping.mappingType === MappingType.ADVANCED && !!attributeMapping.name) {
                const dataFlowExpression = attributeMapping.expression as DataFlowExpression;
                if (!!dataFlowExpression && !!dataFlowExpression.returnType) {
                    const mappedTargetColumn = targetDataFlowSource.schema.find(
                        (dataFlowColumn) => dataFlowColumn.name === attributeMapping.name
                    );
                    if (!mappedTargetColumn) {
                        attributeMapping.name = undefined; // reset the target selection, in case, we find that the target column is not present in target table. Its a rare scenario, should not happen!
                    } else {
                        mappedTargetColumn.dataFlowColumnType = dataFlowExpression.returnType;
                    }
                }
            }
        });
    }

    private _getLinkedService(resourceConnection: Model.AdfDataMapperConnectionResource): LinkedService {
        if (
            resourceConnection &&
            resourceConnection.type === MapperConnectionType.LinkedServiceType &&
            resourceConnection.linkedService
        ) {
            const linkedServiceType = resourceConnection.linkedServiceType;
            if (!!linkedServiceType) {
                const sourceLinkedServiceModel = Metamodel.modelByName(linkedServiceType);
                return ReferenceAdapter.toProxyModel<LinkedService>(
                    sourceLinkedServiceModel,
                    resourceConnection.linkedService, this.context
                );
            }

            return ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                resourceConnection.linkedService, this.context
            );
        }
        return null;
    }

    private _getSourceTable(
        adfDataMapper: AdfDataMapper,
        tableName: string,
        connectionReference: MapperConnectionReference,
        isNonCdcSource: boolean
    ): IMapperTable {
        const connectionsInfo = isNonCdcSource
            ? adfDataMapper.nonCdcSourceConnectionsInfo
            : adfDataMapper.sourceConnectionsInfo;

        if (!!connectionsInfo) {
            const connectionInfo = tableUtils.getTableConnectionInfo(connectionsInfo, connectionReference);

            if (!!connectionInfo) {
                return tableUtils.getMapperTable(connectionInfo.tables, tableName);
            }
        }

        return undefined;
    }

    private _getDslPropertyValue(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mergedDslConnectorProperties: Map<string, any>,
        propertyName: string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): any {
        return !!mergedDslConnectorProperties && mergedDslConnectorProperties.has(propertyName)
            ? mergedDslConnectorProperties.get(propertyName)
            : null;
    }

    private _isCdcSource(sourceConnectionInfo: MappingSourceConnectionInfo): boolean {
        // TODO: change this logic when we have separate API to gnerate dsl for adf data mapper
        // so that we will save non cdc sources in separet section, until backend changes are done
        if (!!sourceConnectionInfo.tables && sourceConnectionInfo.tables.length > 0) {
            const tableDslConnectorProperties = sourceConnectionInfo.tables[0].dslConnectorProperties;
            const commonDslConnectorProperties = sourceConnectionInfo.connection.commonDslConnectorProperties;
            const mergedDictionary = tableUtils.mergeDictionaries(
                tableDslConnectorProperties,
                commonDslConnectorProperties
            );

            if (this._isCdcEnabled(mergedDictionary)) {
                return true;
            }
        }

        return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _isCdcEnabled(mergedDslConnectorProperties: Map<string, any>): boolean {
        const cdcKeys = ['enableCdc', 'enableChangeFeed', 'enableNativeCdc'];
        if (!!mergedDslConnectorProperties) {
            for (const cdcKey of cdcKeys) {
                if (mergedDslConnectorProperties.has(cdcKey) && mergedDslConnectorProperties.get(cdcKey) === true) {
                    return true;
                }
            }
        }

        return false;
    }

    private _isNonCdcSourceConnection(
        resourceConnectionReference: Model.MapperConnectionReferenceResource,
        model: AdfDataMapper
    ): boolean {
        const modelConnectionReference = this._toModelConnectionReference(resourceConnectionReference);
        return tableUtils.getTableConnectionInfo(model.sourceConnectionsInfo, modelConnectionReference) === undefined;
    }

    private _removeRedundantTableDslConnectorProperties(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tableDslConnectorProperties: Map<string, any>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        commonDslConnectorProperties: Map<string, any>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): Map<string, any> {
        if (!!commonDslConnectorProperties && commonDslConnectorProperties.size > 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const newTableDslConnectorProperties = new Map<string, any>();

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            tableDslConnectorProperties.forEach((value: any, key: string) => {
                if (!commonDslConnectorProperties.has(key) || commonDslConnectorProperties.get(key) !== value) {
                    newTableDslConnectorProperties.set(key, value);
                }
            });

            return newTableDslConnectorProperties;
        } else {
            return new Map(Array.from(tableDslConnectorProperties));
        }
    }

    private _convertToMapperCadenceFrequency(frequency: string): MapperCadenceFrequency {
        if (!!frequency) {
            switch (frequency.toLowerCase()) {
                case MapperCadenceFrequency.Hour.toLowerCase():
                    return MapperCadenceFrequency.Hour;
                case MapperCadenceFrequency.Minute.toLowerCase():
                    return MapperCadenceFrequency.Minute;
                case MapperCadenceFrequency.Second.toLowerCase():
                    return MapperCadenceFrequency.Second;
                default:
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    frequency as MapperCadenceFrequency;
            }
        }

        return undefined;
    }

    private _toDataFlowColumnType(dataType: string): IDataFlowDataType {
        if (!!dataType) {
            if (dataType.startsWith('decimal')) {
                const decimalType = new DecimalDataFlowType();
                if (dataType.indexOf(')') >= 0 && dataType.indexOf('(') >= 0 && dataType.indexOf(',') >= 0) {
                    decimalType.precision = +dataType.substring(dataType.indexOf('(') + 1, dataType.indexOf(','));
                    decimalType.scale = +dataType.substring(dataType.indexOf(',') + 1, dataType.indexOf(')'));
                }
                return decimalType;
            }

            return {
                typeString: dataType as unknown as DataFlowDataType,
                typeInDsl: null,
                fullyQualifiedType: dataType as unknown as DataFlowDataType,
                compatibleTypes: [],
                format: null
            };
        }

        return undefined;
    }
}
